export interface ServiceEndpointRef {
  path: string;
  method: string;
  title?: string;
}

export interface ServiceListEntry<T extends ServiceEndpointRef> {
  endpoint: T;
  key: string;
  label: string;
  readableTitle: string;
}

export interface ServiceListGroup<T extends ServiceEndpointRef> {
  label: string;
  endpoints: ServiceListEntry<T>[];
}

const ACRONYMS: Readonly<Record<string, string>> = {
  api: 'API',
  db: 'DB',
  dms: 'DMS',
  dmsf: 'DMSF',
  faq: 'FAQ',
  fdv: 'FDV',
  id: 'ID',
  ids: 'IDs',
  json: 'JSON',
  jsonb: 'JSONB',
  pcs: 'PCS',
  solr: 'Solr',
  ui: 'UI'
};

const SELECTION_SEPARATOR = '\u001f';

export function readablePathTitle(path: string): string {
  const segments = visibleSegments(path);
  if (segments.length === 0) {
    return path;
  }
  return segments.map(readableSegment).join(' / ');
}

export function serviceSelectionKey(method: string, path: string): string {
  return `${method}${SELECTION_SEPARATOR}${path}`;
}

export function apiFilterQuery(value: string, endpoints: readonly ServiceEndpointRef[]): string {
  if (endpoints.some(endpoint => serviceSelectionKey(endpoint.method, endpoint.path) === value)) {
    return '';
  }
  return value;
}

export function hasTypedApiQuery(value: string, endpoints: readonly ServiceEndpointRef[]): boolean {
  return apiFilterQuery(value, endpoints).trim().length > 0;
}

export function restoredSelectionKey(
  keyAtBlur: string | null,
  keyNow: string | null,
  valueNow: string
): string | null {
  if (!keyAtBlur || keyAtBlur !== keyNow || valueNow === keyAtBlur) {
    return null;
  }
  return keyAtBlur;
}

export function groupServiceEndpoints<T extends ServiceEndpointRef>(
  endpoints: readonly T[],
  query: string
): ServiceListGroup<T>[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(token => token.length > 0);
  const grouped = new Map<string, ServiceListEntry<T>[]>();
  for (const endpoint of endpoints) {
    if (!matchesQuery(endpoint, tokens)) {
      continue;
    }
    const label = groupLabel(endpoint.path);
    const entries = grouped.get(label) ?? [];
    entries.push(toEntry(endpoint));
    grouped.set(label, entries);
  }
  return [...grouped.entries()]
    .sort(([left], [right]) => compareText(left, right))
    .map(([label, entries]) => ({
      label,
      endpoints: entries.sort((left, right) =>
        compareText(left.readableTitle, right.readableTitle) || compareText(left.endpoint.method, right.endpoint.method))
    }));
}

function visibleSegments(path: string): string[] {
  return path.split('/').filter(segment => segment.length > 0 && !isVersion(segment));
}

function isVersion(segment: string): boolean {
  return /^v\d+(\.\d+)*$/.test(segment);
}

function groupLabel(path: string): string {
  const first = path.split('/').find(segment => segment.length > 0);
  if (!first) {
    return path;
  }
  return readableSegment(first);
}

function readableSegment(segment: string): string {
  const words = segment.split(/[_-]/).filter(word => word.length > 0);
  if (words.length === 0) {
    return segment;
  }
  return words.map((word, index) => renderWord(word, index === 0)).join(' ');
}

function renderWord(word: string, first: boolean): string {
  const acronym = ACRONYMS[word.toLowerCase()];
  if (acronym) {
    return acronym;
  }
  const lower = word.toLowerCase();
  if (!first) {
    return lower;
  }
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toEntry<T extends ServiceEndpointRef>(endpoint: T): ServiceListEntry<T> {
  const readableTitle = readablePathTitle(endpoint.path);
  return {
    endpoint,
    key: serviceSelectionKey(endpoint.method, endpoint.path),
    readableTitle,
    label: optionLabel(endpoint, readableTitle)
  };
}

function optionLabel(endpoint: ServiceEndpointRef, readableTitle: string): string {
  const base = `${endpoint.method} ${readableTitle}`;
  const title = endpoint.title?.trim() ?? '';
  const lastSegment = visibleSegments(endpoint.path).at(-1);
  if (!title || !lastSegment || title.toLowerCase() === readableSegment(lastSegment).toLowerCase()) {
    return base;
  }
  return `${base} — ${title}`;
}

function matchesQuery(endpoint: ServiceEndpointRef, tokens: string[]): boolean {
  if (tokens.length === 0) {
    return true;
  }
  const haystack = [
    readablePathTitle(endpoint.path),
    endpoint.title ?? '',
    endpoint.path,
    endpoint.method
  ].join(' ').toLowerCase();
  return tokens.every(token => haystack.includes(token));
}

function compareText(left: string, right: string): number {
  if (left < right) {
    return -1;
  }
  if (left > right) {
    return 1;
  }
  return 0;
}
